import React, { useState } from "react";
import { Pressable, StyleSheet, View, Text } from "react-native";
import { ChatsCircle, ListChecks, PushPin, Trash } from "@phosphor-icons/react";
import InlineContextMenu, { InlineContextMenuItem } from "../../axio-ds/Menu/InlineContextMenu";
import { colors, radii, spacing, webTransitions } from "../../../styles/design-system";

export type ThreadType = "task" | "general";

export interface ThreadCardProps {
  id?: string;
  type?: ThreadType;
  title: string;
  metaLabel?: string; // e.g. "10m ago"
  isPinned?: boolean;
  isActive?: boolean;
  disabled?: boolean;
  onPress?: () => void;
  onPinToggle?: () => void;
  onDelete?: () => void;
}

const ThreadCard: React.FC<ThreadCardProps> = ({
  type = "general",
  title,
  metaLabel,
  isPinned = false,
  isActive = false,
  disabled = false,
  onPress,
  onDelete,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const handlePress = () => {
    if (!disabled && onPress) {
      onPress();
    }
  };

  const renderLeftIcon = () => {
    if (isPinned) {
      return <PushPin size={18} weight="fill" color={colors.brand[500]} />;
    }
    if (type === "task") {
      return <ListChecks size={18} color={colors.brand[500]} weight="bold" />;
    }
    return <ChatsCircle size={18} color={colors.brand[500]} weight="bold" />;
  };

  const cardStyle = () => {
    const activeBackground = isActive || isPinned;
    return [styles.cardBase, activeBackground && styles.cardActive, disabled && styles.cardDisabled];
  };

  const menuItems: InlineContextMenuItem[] = [
    {
      id: "delete",
      label: "Delete",
      tone: "danger",
      onPress: onDelete,
      icon: <Trash size={16} color={colors.error[500]} />,
    },
  ];

  return (
    <View
      style={[styles.hoverWrapper, menuOpen && styles.hoverWrapperOpen]}
      onPointerEnter={() => setIsHovered(true)}
      onPointerLeave={() => {
        setIsHovered(false);
        setMenuOpen(false);
      }}>
      <Pressable
        accessibilityRole="button"
        disabled={disabled}
        onPress={handlePress}
        style={cardStyle}
        accessibilityLabel={title}
        accessibilityHint={`Open ${title} thread`}>
        <>
          <View style={styles.leftSection}>
            <View style={styles.iconContainer}>{renderLeftIcon()}</View>
            <View style={styles.textContainer}>
              <Text
                style={styles.title}
                numberOfLines={1}
                accessibilityLabel={title}
                accessibilityRole="text"
                aria-level="3"
                role="heading">
                {title}
              </Text>
              {metaLabel ? (
                <Text style={styles.metaLabel} numberOfLines={1}>
                  {metaLabel}
                </Text>
              ) : null}
            </View>
          </View>
          <View style={[styles.rightSection, (!isHovered || disabled) && styles.rightSectionHidden]}>
            <InlineContextMenu
              items={menuItems}
              open={menuOpen}
              onOpenChange={(open) => {
                if (!disabled) {
                  setMenuOpen(open);
                }
              }}
            />
          </View>
        </>
      </Pressable>
    </View>
  );
};

const styles = StyleSheet.create({
  cardBase: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
    borderRadius: radii.lg,
    borderWidth: 1,
    borderColor: colors.grey[200],
    backgroundColor: colors.white,
    marginBottom: spacing.xs,
    ...webTransitions.fast,
  },
  cardActive: {
    backgroundColor: colors.brand[25],
    borderColor: colors.brand[500],
  },
  cardDisabled: {
    opacity: 0.5,
  },
  hoverWrapper: {
    alignSelf: "stretch",
    paddingVertical: spacing.xs, // safety margin on the right
    position: "relative",
    zIndex: 0,
  },
  hoverWrapperOpen: {
    zIndex: 2,
  },
  leftSection: {
    flexDirection: "row",
    alignItems: "center",
    flexShrink: 1,
  },
  rightSection: {
    marginLeft: spacing.xs,
    padding: spacing.xs,
    ...webTransitions.fast,
  },
  rightSectionHidden: {
    opacity: 0,
    pointerEvents: "none",
  },
  iconContainer: {
    width: 32,
    height: 32,
    borderRadius: radii.lg,
    backgroundColor: colors.grey[100],
    alignItems: "center",
    justifyContent: "center",
    marginRight: spacing.sm,
  },
  textContainer: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontSize: 14,
    fontWeight: "500",
    fontFamily: "Inter",
    color: colors.grey[900],
  },
  metaLabel: {
    fontSize: 12,
    fontFamily: "Inter",
    color: colors.grey[600],
    marginTop: 2,
  },
});

export default ThreadCard;
